import React from "react";
import { Box, Flex, HStack, Text, Circle, IconButton } from "@chakra-ui/react";
import { FiMoreVertical } from "react-icons/fi";
import Countdown from "react-countdown";
import dayjs from "dayjs";
import {
  ActivityResultAccordion,
  ActivityCardDropdown,
  ActivityTypeIcon,
} from "@components/Activity";
import { getLiveButtonProps } from "@components/Activity/helpers";
import { RemixBadge } from "@components/Agenda";
import { getActivityBgColor } from "@utils/theme/colors";
import Duration from "@components/Duration";
import { activityTypes } from "@constants/activities";

// Helper function to format duration
const formatDuration = (timestamp) => {
  // If timestamp is a number (seconds), format as MM:SS
  if (typeof timestamp === "number") {
    const minutes = Math.floor(timestamp / 60);
    const seconds = timestamp % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  }

  // Handle string timestamps (legacy support)
  if (typeof timestamp === "string") {
    // Handle ISO date strings like "2025-09-10T07:00"
    if (timestamp.includes("T")) {
      const date = new Date(timestamp);
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();
      return `${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
    }
    // Handle MM:SS format
    const [minutes] = timestamp.split(":");
    return `${minutes}:00`;
  }

  return "00:00";
};

// Helper function to calculate relative time from agenda start
const calculateRelativeTime = (activityStartTime, agendaStartTime) => {
  if (!activityStartTime || !agendaStartTime) return null;

  const agendaStart = new Date(agendaStartTime).getTime();
  const activityStart = new Date(activityStartTime).getTime();
  const relativeTimeMs = Math.max(0, activityStart - agendaStart);

  return Math.floor(relativeTimeMs / 1000); // Return seconds
};

/**
 * Pure UI component for Activity Card - only receives props, no logic
 */
const ActivityCardUI = ({
  // Activity data
  activity,
  index = 0,

  // Required props for getLiveButtonProps
  agenda,
  meetingParticipants = [],
  hasLiveActivity,
  isGoingLive,
  startActivity,
  stopActivity,

  // Display options
  simplified = false,
  showAccordion = false,
  isAccordionOpen = false,

  // Event handlers
  onPlayClick,
  onMoreOptionsClick,
  onToggleAccordion,

  // Dropdown props
  isDropdownOpen = false,
  onDropdownClose,
  dropdownPosition = { x: 0, y: 0 },
  onInsight,
  onRemix,
  onEdit,
  onDelete,
  isActivityEditable = false,

  // Status
  isLoading = false,
  isRemixing = false,
  isDisabled = false,
  hasResponses = false,
}) => {
  const handleRemixClick = () => {
    onRemix?.();
  };

  // Get button props from getLiveButtonProps helper
  const buttonProps = getLiveButtonProps(
    activity,
    agenda?.live,
    hasLiveActivity,
    startActivity,
    stopActivity,
    isGoingLive
  );

  // Simplified design for agenda view
  if (simplified) {
    const showFooter = true; // Always show the footer for accordion functionality
    const isPlayed = !!activity.endTime; // Check if activity has been played

    return (
      <>
        <Box>
          <Box
            bg="white"
            borderRadius={showFooter ? "8px 8px 0 0" : "8px"}
            p={3}
            boxShadow="0px 2px 4px rgba(12, 0, 28, 0.04), 0px 0px 6px rgba(12, 0, 28, 0.02)"
            _hover={{ boxShadow: "md" }}
            transition="all 0.2s"
            data-testid={`activity-card-${activity._id}`}
          >
            <Flex justify="space-between" align="center">
              <HStack spacing={3} flex={1}>
                <Circle
                  size="33px"
                  bg={
                    isPlayed
                      ? "#E0E0E0"
                      : getActivityBgColor(activity.type, activity)
                  }
                  opacity={isPlayed ? 0.6 : 1}
                >
                  <ActivityTypeIcon
                    type={activity.type}
                    activity={activity}
                    size="16px"
                  />
                </Circle>
                <Box flex={1}>
                  <Text
                    fontSize="12px"
                    color={isPlayed ? "#98A2B3" : "dark.500"}
                    fontWeight={400}
                    noOfLines={1}
                    data-testid="activity-card-title"
                  >
                    {activity.title || activity.content || "Activity"}
                  </Text>
                  <RemixBadge
                    visible={activity.remixInfo?.status === "remixed"}
                    text="Remixed!"
                    size="sm"
                    variant="default"
                  />
                </Box>
              </HStack>

              <HStack>
                {(() => {
                  // Use stored relativeStartTime if available, otherwise calculate it
                  const relativeTime =
                    activity.relativeStartTime !== undefined &&
                    activity.relativeStartTime !== null
                      ? activity.relativeStartTime
                      : activity.startTime && agenda?.startTime
                      ? calculateRelativeTime(
                          activity.startTime,
                          agenda.startTime
                        )
                      : null;

                  // Activity is currently running
                  if (activity.live) {
                    if (activity.type === "share") {
                      // Share type: show relative start time immediately
                      return relativeTime !== null ? (
                        <Box bg="#F6F5F7" px={2} py={1} borderRadius="6px">
                          <Text
                            fontSize="11px"
                            color="#363636"
                            fontWeight={500}
                          >
                            {formatDuration(relativeTime)}
                          </Text>
                        </Box>
                      ) : null;
                    } else {
                      // Other types: show countdown
                      return (
                        <Box bg="#F6F5F7" px={2} py={1} borderRadius="6px">
                          <Countdown
                            date={dayjs(activity.startTime || Date.now())
                              .add(activity.settings?.duration || 2, "minute")
                              .toDate()}
                            renderer={Duration}
                          />
                        </Box>
                      );
                    }
                  }
                  // Activity has ended: show relative start time
                  else if (activity.endTime && relativeTime !== null) {
                    return (
                      <Box bg="#F6F5F7" px={2} py={1} borderRadius="6px">
                        <Text fontSize="11px" color="#363636" fontWeight={500}>
                          {formatDuration(relativeTime)}
                        </Text>
                      </Box>
                    );
                  }
                  // Activity not started: hide scheduled timestamp (AI-generated)
                  // else if (activity.timestamp) {
                  //   return (
                  //     <Box bg="#F6F5F7" px={2} py={1} borderRadius="6px">
                  //       <Text fontSize="11px" color="#363636" fontWeight={500}>
                  //         {formatDuration(activity.timestamp)}
                  //       </Text>
                  //     </Box>
                  //   );
                  // }
                  return null;
                })()}
                <Box
                  cursor={buttonProps.disabled ? "not-allowed" : "pointer"}
                  opacity={buttonProps.disabled ? 0.6 : 1}
                  onClick={
                    !buttonProps.disabled ? buttonProps.onClick : undefined
                  }
                  aria-label={buttonProps["aria-label"]}
                  data-testid={`activity-live-button-${activity._id}`}
                >
                  {buttonProps.icon}
                </Box>
                <IconButton
                  icon={<FiMoreVertical />}
                  size="sm"
                  variant="ghost"
                  color="black"
                  aria-label="More options"
                  isDisabled={isDisabled}
                  onClick={onMoreOptionsClick}
                />
              </HStack>
            </Flex>
          </Box>

          {/* Accordion footer for simplified design */}
          {showFooter && (
            <Box
              bg="white"
              borderRadius="0 0 8px 8px"
              borderTop="1px solid"
              borderColor="#F6F5F7"
              py={2}
              px={3}
              boxShadow="0px 2px 4px rgba(12, 0, 28, 0.04)"
            >
              {/* Activity Result Accordion */}
              <ActivityResultAccordion
                options={activity.options || []}
                type={activity.type}
                response={activity.response || []}
                settings={activity.settings}
                title={activity.title}
                content={activity.content}
                link={activity.link}
                numberOfParticipants={meetingParticipants?.length || 0}
                participants={meetingParticipants}
                activityId={activity._id}
                isAccordionOpen={isAccordionOpen}
                toggleAccordion={onToggleAccordion}
                isAgendaLive={agenda?.live}
                activity={activity}
              />
            </Box>
          )}
        </Box>

        {/* Dropdown component for simplified design */}
        <ActivityCardDropdown
          isOpen={isDropdownOpen}
          onClose={onDropdownClose}
          onInsight={onInsight}
          onRemix={handleRemixClick}
          onEdit={onEdit}
          onDelete={onDelete}
          position={dropdownPosition}
          activity={activity}
          isActivityEditable={isActivityEditable}
        />
      </>
    );
  }
};

export default ActivityCardUI;
